import {
  AgentPolicyControllerService as Policy,
  type AgentPolicyRequestModel,
  type DataAccessPolicyRuleColumns,
} from "@/api";
import {
  type AgentPoliciesQuery,
  type AgentPolicyInput,
  type AgentPolicyRule,
  type AgentPolicyStatus,
  type AuthoredAgentPolicy,
  type CreateAgentPolicyRequest,
  isServerScopedRule,
  ruleTargetToolIds,
  type DeleteAgentPolicyRequest,
  type UpdateAgentPolicyRequest,
} from "@/api/temp/AgentPoliciesService";
import type { RequestOptions } from "@/hooks/usePaginateQuery";
import { AgentDetailsApi } from "@/pages/AgentEstate/agentApi";
import { columnRulesToConditionEntries } from "@/pages/MCPRegistry/utils/mapPolicyFormValues";
import { mapConditionEntriesToColumnRules } from "@/pages/MCPRegistry/utils/mapPolicyPayload";

/**
 * A rule as the API reads and writes it (TRUST-13500): its conditions as the
 * policy column rules a data access policy stores (`columnRules`, joined by
 * `conjunction`), not the ConditionBuilder entries the editor works with.
 */
type ApiRule = Omit<AgentPolicyRule, "combinator" | "conditions"> & {
  conjunction?: "AND" | "OR";
  /** `value` is typed loosely: the generated model reads it as an object. */
  columnRules?: Array<
    Omit<DataAccessPolicyRuleColumns, "value"> & { value?: unknown }
  >;
  /** Older responses named them as the editor does. */
  combinator?: "AND" | "OR";
  conditions?: AgentPolicyRule["conditions"];
};

/** Thrown when a server-wide rule's server has no tools to save it as. */
export const SERVER_HAS_NO_TOOLS = "AGENT_POLICY_SERVER_HAS_NO_TOOLS";

/**
 * The editor's rule as the API takes it. The API's `Rule` has no `scope`, and
 * a new rule has no id yet, so neither is sent.
 */
const toApiRule = (rule: AgentPolicyInput["rules"][number]) => {
  const { combinator, conditions, scope: _scope, id, ...rest } = rule;
  const conjunction = combinator === "OR" ? "OR" : "AND";
  return {
    ...(id && { id }),
    ...rest,
    conjunction,
    columnRules: mapConditionEntriesToColumnRules(
      conditions ?? [],
      conjunction,
    ),
  };
};

/**
 * The API has no rule `scope`, so a server-wide rule is saved as the server's
 * current tools: the caller's copy of the agent's servers, else read from
 * agent details - only when a rule needs them.
 */
const toApiPolicy = async ({
  agentId,
  policy,
  mcpServices,
}: Pick<CreateAgentPolicyRequest, "agentId" | "policy" | "mcpServices">) => {
  const services = policy.rules.some(isServerScopedRule)
    ? (mcpServices ??
      (await AgentDetailsApi.getEstateAgentUsingGet({ agentId }))
        ?.mcpServices ??
      [])
    : [];
  return {
    ...policy,
    rules: policy.rules.map(rule => {
      const toolIds = ruleTargetToolIds(rule, services);
      // An empty list would save a rule that protects nothing.
      if (isServerScopedRule(rule) && !toolIds.length)
        throw Object.assign(new Error(), { code: SERVER_HAS_NO_TOOLS });
      return toApiRule({ ...rule, toolIds });
    }),
  };
};

/**
 * A condition's value as the editor reads it: a comma-joined string. The
 * generated model types it as an object, so a list or object is flattened
 * here rather than crashing the editor.
 */
const valueString = (value: unknown): string =>
  Array.isArray(value)
    ? value.join(",")
    : value == null
      ? ""
      : typeof value === "object"
        ? JSON.stringify(value)
        : String(value);

/**
 * Condition ids derived from the rule, so a refetch of an unchanged policy is
 * deep-equal to the last one and React Query keeps the cached objects.
 */
const conditionIds = (prefix: string) => {
  let next = 0;
  return () => `${prefix}:cond-${next++}`;
};

/**
 * The API's rule as the editor reads it. A conditional rule reads back as
 * ALLOW_ONLY_WHEN; an agent policy shows it as ALLOW with its conditions.
 */
const fromApiRule = (
  rule: ApiRule,
  index: number,
  policyId: string | undefined,
  customization: boolean,
): AgentPolicyRule => {
  const { conjunction, columnRules, combinator, conditions, ...rest } = rule;
  return {
    ...rest,
    toolIds: rest.toolIds ?? [],
    effect:
      rest.effect === "ALLOW_ONLY_WHEN" && !customization
        ? "ALLOW"
        : rest.effect,
    combinator: (conjunction ?? combinator) === "OR" ? "OR" : "AND",
    conditions: columnRules?.length
      ? columnRulesToConditionEntries(
          columnRules.map(column => ({
            ...column,
            value: valueString(column.value),
          })),
          conditionIds(`${policyId}:${rest.id ?? index}`),
        )
      : (conditions ?? []),
  };
};

/**
 * The API's policy as the editor reads it, its arrays defaulted so an older
 * response cannot crash it.
 */
const asPolicy = (policy: unknown): AuthoredAgentPolicy => {
  const item = policy as Partial<AuthoredAgentPolicy> & { rules?: ApiRule[] };
  const customization = item.origin === "TOOL_CUSTOMIZATION";
  return {
    ...item,
    rules: (item.rules ?? []).map((rule, index) =>
      fromApiRule(rule, index, item.id, customization),
    ),
    inheritedPolicyIds: item.inheritedPolicyIds ?? [],
  } as AuthoredAgentPolicy;
};

/**
 * The request body: the backend takes the authored policy on the generated
 * agent_policies routes (it tells it from the IBAC body by its rules), so it is
 * not mapped onto that model.
 */
const asRequestModel = (body: object) =>
  body as unknown as AgentPolicyRequestModel;

const STATUSES: ReadonlySet<string> = new Set<AgentPolicyStatus>([
  "DRAFT",
  "APPLIED",
]);

/**
 * The saved policy, with the status the server reports (`policyStatus` on
 * the response model) when it is one the editor knows; else the request's, so
 * the toast says applied or draft as asked (APPLIED saves and applies).
 */
const asSaved = (
  response: unknown,
  status: CreateAgentPolicyRequest["status"],
) => {
  const saved = asPolicy(response);
  const reported = [
    saved.status,
    (response as { policyStatus?: unknown } | undefined)?.policyStatus,
  ].find(
    (value): value is AgentPolicyStatus =>
      typeof value === "string" && STATUSES.has(value),
  );
  return { ...saved, status: reported ?? status };
};

/**
 * The authored agent policy APIs behind the demo's method names (the mock and
 * query keys). Shorthand methods, so the minifier keeps those names. The agent
 * id is its application id.
 */
export const AuthoredPolicyApi = {
  async listAgentPoliciesUsingGet(
    { agentId }: AgentPoliciesQuery,
    options?: RequestOptions,
  ) {
    // authored_agent_policies: the agent's policies with their rules (the
    // agent_policies list is the IBAC one, without rules).
    const policies = await Policy.listAgentPoliciesUsingGet(
      { applicationId: agentId },
      options,
    );
    return (policies ?? []).map(asPolicy);
  },
  async createAgentPolicyUsingPost(request: CreateAgentPolicyRequest) {
    const { agentId, status } = request;
    return asSaved(
      await Policy.createAgentPolicyUsingPost({
        applicationId: agentId,
        requestModel: asRequestModel({
          agentId,
          status,
          policy: await toApiPolicy(request),
        }),
      }),
      status,
    );
  },
  async updateAgentPolicyUsingPut(request: UpdateAgentPolicyRequest) {
    const { agentId, policyId, version, status } = request;
    return asSaved(
      await Policy.updateAgentPolicyUsingPut({
        applicationId: agentId,
        id: policyId,
        requestModel: asRequestModel({
          agentId,
          policyId,
          version,
          status,
          policy: await toApiPolicy(request),
        }),
      }),
      status,
    );
  },
  async deleteAgentPolicyUsingDelete({
    agentId,
    policyId,
  }: DeleteAgentPolicyRequest) {
    await Policy.deleteAgentPolicyUsingDelete({
      applicationId: agentId,
      id: policyId,
    });
    return { policyId };
  },
};
